import React, { useMemo } from 'react';

import { Button, Callout, Text } from '@fly/core';
import { COOKIE_NAMES, GeneratedToken, getCookie } from '@fly/shared';
import { FlyCodeCopy, FlyMarkdownRenderer } from '@fly/shared-ui';
import { t } from 'i18next';

export interface TokenProps {
  token: GeneratedToken;
  envName: string;
  onClose: () => void;
}

const kubectlCommand = `
\`\`\`bash
kubectl create secret docker-registry <staging-fly-token> \\
  --docker-server=<your-fly-subdomain>.jfrog.io/docker \\
  --docker-username=<your-fly-username> \\
  --docker-password=<your-fly-token> \\
  --namespace=<namespace>
\`\`\`
`;

const getKubectlCommand = (token: GeneratedToken, env: string) => {
  return kubectlCommand
    .replace('<staging-fly-token>', `${env}-fly-token`)
    .replace('<your-fly-subdomain>', getCookie(COOKIE_NAMES.TEAM_NAME) || '')
    .replace('<your-fly-username>', token.username)
    .replace('<your-fly-token>', token.accessToken);
};

export function Token({ token, envName, onClose }: TokenProps) {
  const command = useMemo(
    () => getKubectlCommand(token, envName),
    [token, envName]
  );

  return (
    <div data-testid='runtime-token' className='flex flex-col gap-fly-3'>
      <div data-testid='runtime-token-title' className='gap-fly-2'>
        <Text size='2'>
          {t('app.dashboard.runtime_environments.token.pull_token', {
            defaultValue: 'Image pull token',
          })}
        </Text>
        <br />
        <Text size='2' color='neutral'>
          {t('app.dashboard.runtime_environments.token.pull_description', {
            environment: envName,
          })}
        </Text>
      </div>
      <div
        data-testid='runtime-token-token'
        className='flex flex-col gap-fly-3'
      >
        <div className='mt-fly-3 truncate'>
          <FlyCodeCopy
            data-testid='copy-token-btn'
            className='w-full max-w-full !bg-[var(--slate-a3)]'
            code={token.accessToken}
          />
        </div>
        <Callout
          data-testid='runtime-token-info-message'
          variant='surface'
          icon='info_circled'
          color='info'
          size='1'
          className='flex'
          title={t('app.dashboard.runtime_environments.token.info', {
            defaultValue: 'Once closed we will not be able to show it again',
          })}
        />
      </div>
      <div
        data-testid='runtime-token-kubectl-config'
        className='flex flex-col gap-fly-3'
      >
        <div className='mt-fly-6 flex flex-col gap-fly-3'>
          <Text size='2'>
            {t('app.dashboard.runtime_environments.token.k8s_config.title', {
              defaultValue: 'Configure Kubernetes',
            })}
          </Text>
          <Text size='1' className='text-slate-11'>
            {t('app.dashboard.runtime_environments.token.k8s_config.subtitle', {
              defaultValue: 'Fly Registry credentials, with image pull secre',
            })}
          </Text>
          <FlyMarkdownRenderer
            markdown={command}
            className='!mt-fly-3 [&_pre]:!border-none [&_pre]:!bg-[var(--slate-a3)]'
          />
        </div>
        <Callout
          data-testid='runtime-token-info-message'
          icon='info_circled'
          color='neutral'
          size='1'
          variant='surface'
          className='flex'
          title={t('app.dashboard.runtime_environments.token.note', {
            defaultValue: 'Note, the namespace should be manually adjusted',
          })}
        />
      </div>
      <Button
        variant='soft'
        size='2'
        color='neutral'
        className='mt-fly-6'
        onClick={onClose}
      >
        {t('app.dashboard.runtime_environments.token.close', {
          defaultValue: 'Close',
        })}
      </Button>
    </div>
  );
}
